import type { ProgramFixedContentRow, ProgramProgressCell, ProgramScopedContentRow } from '../types/domain';
import { programProgressCellToProgressLike, type ProgramProgressLikeRow } from './annualProgressFieldDisplay';
import { fixedLearningPlainPrefixEventName } from './programCellDisplayText';
import {
  cellLearningTextForEditor,
  mergeProgramLearningContentFields,
  serializeProgramLearningContentFieldsForEditor,
} from './programLearningContentFields';
import { getProgramWorkspaceScopeEntry } from './programEditWorkspace';
import { readValidScopedContentRowsFromLocalCache } from './programScopedContentLsBridge';
import { mergeProgramWorkspaceUpsertsIntoBaselineRows } from './programWorkspaceDisplayMerge';
import type { TrackProgramMode } from './trackProgramModeStorage';
import { isSyncTrackMirrorScope, overlaySyncMirrorProgramContentFromRepresentative } from './trackSyncScopedContent';
import {
  compareContentRowsByOrder,
  sortContentRowsByOrder,
  type ScopedScheduleSlotRef,
} from './scheduleContentMatcher';

export function normalizeFixedEventName(raw: string): string {
  return raw.trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
}

export function collectFixedProgramIdentitySets(
  rows: ReadonlyArray<{
    source_annual_cell_key?: string | null;
    event_name?: string | null;
    unit_text?: string | null;
  }>
): { annualKeys: Set<string>; eventNames: Set<string> } {
  const annualKeys = new Set<string>();
  const eventNames = new Set<string>();
  for (const row of rows) {
    const key = (row.source_annual_cell_key ?? '').trim();
    if (key) annualKeys.add(key);
    const fromEvent = normalizeFixedEventName(row.event_name ?? '');
    if (fromEvent) eventNames.add(fromEvent);
    const unit = (row.unit_text ?? '').trim();
    if (unit.startsWith('◆')) {
      const fromUnit = normalizeFixedEventName(unit);
      if (fromUnit) eventNames.add(fromUnit);
    }
  }
  return { annualKeys, eventNames };
}

/** 연간 셀 키 → 이벤트명 → 시간표명 순으로 고정행을 찾는다. */
export function pickFixedContentRowForAnnualSlot(
  fixedRows: ProgramFixedContentRow[],
  annualCellKey: string,
  eventKey: string,
  timetableTextRaw: string
): ProgramFixedContentRow | null {
  const cellKey = annualCellKey.trim();
  if (cellKey) {
    const byCell = fixedRows.find((r) => (r.source_annual_cell_key ?? '').trim() === cellKey);
    if (byCell) return byCell;
  }
  const event = eventKey.trim();
  if (event) {
    const byEvent = fixedRows.find((r) => eventNameFromFixedContentRow(r) === event);
    if (byEvent) return byEvent;
  }
  const tt = timetableTextRaw.trim();
  if (tt) {
    const byTimetable = fixedRows.find((r) => (r.timetable_text ?? '').trim() === tt);
    if (byTimetable) return byTimetable;
  }
  return null;
}

export function eventNameFromFixedContentRow(row: ProgramFixedContentRow): string {
  const byEvent = normalizeFixedEventName(row.event_name ?? '');
  if (byEvent) return byEvent;
  const byUnit = normalizeFixedEventName(row.unit_text ?? '');
  if (byUnit) return byUnit;
  const learning = (row.learning_text ?? '').trim();
  const m = learning.match(/^◆\s*([^-]+?)\s*-/);
  return m?.[1] ? normalizeFixedEventName(m[1]) : '';
}

export function annualSlotHasTimetableProgramAssignment(displayText: string, displayColor: string): boolean {
  return displayText.trim().length > 0 || displayColor.trim().length > 0;
}

export function scopedRowToProgressLike(row: ProgramScopedContentRow): ProgramProgressLikeRow {
  return {
    unit_text: row.unit_text,
    /** DB는 learning_text=본문, ref는 별도 컬럼 — 표시·인디케이터용으로 합친 JSON */
    learning_text: cellLearningTextForEditor(row),
    page_text: row.page_text,
    assistant_page_text: row.assistant_page_text,
    current_period_text: row.current_period_text,
    total_period_text: row.total_period_text,
    material_text: row.material_text,
    record_cross_curricular_text: row.record_cross_curricular_text,
    record_memo_text: row.record_memo_text,
    record_evaluation_text: row.record_evaluation_text,
    record_class_text: row.record_class_text,
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
}

function pickNonEmptyProgramContentField(
  scopedVal: string | null | undefined,
  legacyVal: string | null | undefined
): string {
  const scoped = (scopedVal ?? '').trim();
  const legacy = (legacyVal ?? '').trim();
  return scoped || legacy || '';
}

/** 고정 프로그램 시드 본문(`◆행사명-`). 색을 지운 뒤 저장하면 칸 값은 지운다. */
export function isFixedProgramSeedLearningText(raw: string | null | undefined): boolean {
  return Boolean(fixedLearningPlainPrefixEventName(raw));
}

function legacyCellForScopedLearningMerge(legacyCell: ProgramProgressCell): ProgramProgressCell {
  if (!isFixedProgramSeedLearningText(legacyCell.learning_text)) return legacyCell;
  return { ...legacyCell, learning_text: '' };
}

export function mergeScopedRowWithLegacyProgramCell(
  scopedRow: ProgramScopedContentRow,
  legacyCell: ProgramProgressCell | undefined
): ProgramProgressLikeRow {
  const scoped = scopedRowToProgressLike(scopedRow);
  if (!legacyCell) return scoped;
  const legacy = programProgressCellToProgressLike(legacyCell);
  const legacyForLearning = legacyCellForScopedLearningMerge(legacyCell);
  return {
    unit_text: pickNonEmptyProgramContentField(scoped.unit_text, legacy.unit_text),
    /**
     * 단원·쪽수·차시는 scoped 우선(비면 레거시).
     * 학습내용은 later-wins merge라 순서를 (레거시 → scoped)로 두고,
     * 고정 프로그램 시드 본문은 일반 편제 칸에서 쓰지 않는다.
     */
    learning_text: serializeProgramLearningContentFieldsForEditor(
      mergeProgramLearningContentFields(legacyForLearning, scopedRow)
    ),
    page_text: pickNonEmptyProgramContentField(scoped.page_text, legacy.page_text),
    assistant_page_text: pickNonEmptyProgramContentField(scoped.assistant_page_text, legacy.assistant_page_text),
    current_period_text: pickNonEmptyProgramContentField(scoped.current_period_text, legacy.current_period_text),
    total_period_text: pickNonEmptyProgramContentField(scoped.total_period_text, legacy.total_period_text),
    material_text: pickNonEmptyProgramContentField(scoped.material_text, legacy.material_text),
    record_cross_curricular_text: pickNonEmptyProgramContentField(
      scoped.record_cross_curricular_text,
      legacy.record_cross_curricular_text
    ),
    record_memo_text: pickNonEmptyProgramContentField(scoped.record_memo_text, legacy.record_memo_text),
    record_evaluation_text: pickNonEmptyProgramContentField(scoped.record_evaluation_text, legacy.record_evaluation_text),
    record_class_text: pickNonEmptyProgramContentField(scoped.record_class_text, legacy.record_class_text),
    record_member_group_id: scoped.record_member_group_id ?? legacy.record_member_group_id ?? null,
    /** 배경색은 지운 상태도 의미가 있어 레거시로 채우지 않는다 */
    bg_color_by_field: scoped.bg_color_by_field ?? {},
  };
}

function sortScopedRowsByOrder(rows: ProgramScopedContentRow[]): ProgramScopedContentRow[] {
  return sortContentRowsByOrder(rows);
}

function buildTrackProgramWorkspaceScopeId(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  termNumber: 1 | 2,
  scopeKey: string
): string {
  return `track|${workspaceId}|${gradeId}|${academicYear}|${termNumber}|${scopeKey}`;
}

function mergeTrackScopedRowsForScopeDisplay(
  rows: ProgramScopedContentRow[],
  input: {
    workspaceId: string;
    gradeId: string;
    academicYear: number;
    termNumber: 1 | 2;
    scopeKey: string;
  }
): ProgramScopedContentRow[] {
  const sorted = sortScopedRowsByOrder(rows);
  /**
   * 연간진도표 baseline은 현재 메모리 rows(서버/RQ 기준)를 우선한다.
   * LS 캐시는 쿼리 초기 부팅처럼 rows가 비어 있을 때만 fallback으로 사용해
   * stale LS가 단원·쪽수 등 본문을 덮어쓰는 회귀를 막는다.
   */
  const fromLs =
    sorted.length > 0
      ? { found: false as const, rows: [] as ProgramScopedContentRow[] }
      : readValidScopedContentRowsFromLocalCache(
          input.workspaceId,
          input.academicYear,
          input.termNumber,
          input.gradeId,
          'track',
          input.scopeKey
        );
  let baseline = fromLs.found && fromLs.rows.length ? sortScopedRowsByOrder(fromLs.rows) : sorted;
  const scopeId = buildTrackProgramWorkspaceScopeId(
    input.workspaceId,
    input.gradeId,
    input.academicYear,
    input.termNumber,
    input.scopeKey
  );
  const entry = getProgramWorkspaceScopeEntry(scopeId);
  if (entry?.isDirty && entry.rows.length) {
    baseline = mergeProgramWorkspaceUpsertsIntoBaselineRows(baseline, entry.rows, {
      persistKind: entry.persistKind === 'replace' ? 'replace' : 'patch',
      identity: 'id-position',
    }) as ProgramScopedContentRow[];
  }
  return baseline;
}

/**
 * 연간진도표: 프로그램 표와 같이 워크스페이스 더티·스코프 LS·동기화 미러 fan-out을 반영한 scoped 행.
 * (통합 저장 전에도 대표 시간표 편집 내용이 미러 alias에 표시되도록)
 */
export function enrichScopedRowsForAnnualProgressDisplay(
  scopedRows: ProgramScopedContentRow[],
  input: {
    workspaceId: string;
    gradeId: string;
    academicYear: number;
    termNumber: 1 | 2;
    trackProgramModeByTrack: Record<string, TrackProgramMode>;
    trackProgramSyncRepByTrack: Record<string, string>;
  }
): ProgramScopedContentRow[] {
  const { workspaceId, gradeId, academicYear, termNumber, trackProgramModeByTrack, trackProgramSyncRepByTrack } =
    input;
  if (!workspaceId.trim() || !gradeId.trim() || !scopedRows.length) return scopedRows;

  const byScopeKey = new Map<string, ProgramScopedContentRow[]>();
  for (const row of scopedRows) {
    if (row.scope_type !== 'track') continue;
    const sk = (row.scope_key ?? '').trim();
    if (!sk) continue;
    const arr = byScopeKey.get(sk) ?? [];
    arr.push(row);
    byScopeKey.set(sk, arr);
  }
  if (!byScopeKey.size) return scopedRows;

  const mergedByScope = new Map<string, ProgramScopedContentRow[]>();
  for (const [scopeKey, rows] of Array.from(byScopeKey.entries())) {
    mergedByScope.set(
      scopeKey,
      mergeTrackScopedRowsForScopeDisplay(rows, { workspaceId, gradeId, academicYear, termNumber, scopeKey })
    );
  }

  const templates = new Set<string>();
  Array.from(mergedByScope.keys()).forEach((scopeKey) => {
    const { trackName } = parseTrackScopeKey(scopeKey);
    if (trackName) templates.add(trackName);
  });

  Array.from(templates).forEach((template) => {
    if ((trackProgramModeByTrack[template] ?? 'unified') !== 'sync') return;
    const syncRep = trackProgramSyncRepByTrack[template]?.trim();
    if (!syncRep) return;
    const repKey = `${template}|${syncRep}`;
    const repRows = mergedByScope.get(repKey) ?? [];
    if (!repRows.length) return;

    Array.from(mergedByScope.entries()).forEach(([scopeKey, mirrorRows]) => {
      if (scopeKey === repKey) return;
      const { trackName, timetableAlias } = parseTrackScopeKey(scopeKey);
      if (trackName !== template || !timetableAlias || timetableAlias === syncRep) return;
      mergedByScope.set(
        scopeKey,
        overlaySyncMirrorProgramContentFromRepresentative(mirrorRows, repRows)
      );
    });
  });

  const trackRowsOut: ProgramScopedContentRow[] = [];
  mergedByScope.forEach((rows) => {
    trackRowsOut.push(...rows);
  });
  const nonTrack = scopedRows.filter((r) => r.scope_type !== 'track');
  return [...nonTrack, ...trackRowsOut];
}

/** 동기화 미러 슬롯: 대표 scope의 N번째 행(미러 occurrence와 짝) */
export function pickRepScopedContentRowForMirrorSlot(
  index: ScopedRowsAnnualIndex,
  preferredTrackScopeKey: string,
  scopedOccurrenceIndex: number | undefined
): ProgramScopedContentRow | null {
  const preferred = preferredTrackScopeKey.trim();
  if (!preferred || scopedOccurrenceIndex === undefined) return null;
  return index.trackRowsByScopeKey.get(preferred)?.[scopedOccurrenceIndex] ?? null;
}

/** 동기화 미러: 내용은 대표 행, 수업기록은 alias 행 */
export function mergeSyncTrackScopedRowsForDisplay(
  contentRow: ProgramScopedContentRow,
  recordRow: ProgramScopedContentRow | null | undefined
): ProgramProgressLikeRow {
  const base = scopedRowToProgressLike(contentRow);
  if (!recordRow) {
    return { ...base, record_class_text: '', record_member_group_id: null };
  }
  return {
    ...base,
    record_class_text: recordRow.record_class_text,
    record_member_group_id: recordRow.record_member_group_id ?? null,
  };
}

/** 수업기록 저장·표시용 scoped 행(동기화 미러는 alias scope) */
export function pickScopedRecordRowFromAnnualIndex(
  index: ScopedRowsAnnualIndex,
  annualCellKey: string,
  programKindLabel: string,
  templateLabel: string,
  timetableText: string,
  projectId: string | null,
  legacyTimetableText: string | undefined,
  trackOccurrenceIndex: number | undefined,
  scopeLookupKey: string,
  preferredTrackScopeKey: string,
  trackProgramModeByTrack: Record<string, TrackProgramMode>
): ProgramScopedContentRow | null {
  const recordScopeKey = isSyncTrackMirrorScope(
    templateLabel,
    scopeLookupKey,
    preferredTrackScopeKey,
    trackProgramModeByTrack
  )
    ? scopeLookupKey
    : preferredTrackScopeKey;
  if (!recordScopeKey) return null;
  return pickScopedContentFromAnnualIndex(
    index,
    annualCellKey,
    programKindLabel,
    templateLabel,
    timetableText,
    projectId,
    legacyTimetableText,
    trackOccurrenceIndex,
    recordScopeKey
  );
}

export function fixedContentRowToProgressLike(row: ProgramFixedContentRow): ProgramProgressLikeRow {
  return {
    unit_text: row.unit_text,
    learning_text: cellLearningTextForEditor(row),
    page_text: row.page_text,
    assistant_page_text: row.assistant_page_text,
    current_period_text: row.current_period_text,
    total_period_text: row.total_period_text,
    material_text: row.material_text,
    record_cross_curricular_text: row.record_cross_curricular_text,
    record_memo_text: row.record_memo_text,
    record_evaluation_text: row.record_evaluation_text,
    record_class_text: row.record_class_text,
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
}

export function mergeFixedContentWithLegacyProgramCell(
  fixedRow: ProgramFixedContentRow,
  legacyCell: ProgramProgressCell | undefined
): ProgramProgressLikeRow {
  const base = fixedContentRowToProgressLike(fixedRow);
  if (!legacyCell) return base;
  return {
    ...base,
    record_class_text: legacyCell.record_class_text?.trim()
      ? legacyCell.record_class_text
      : base.record_class_text,
    record_member_group_id: legacyCell.record_member_group_id ?? base.record_member_group_id ?? null,
  };
}

function parseTrackScopeKey(scopeKeyRaw: string): { trackName: string; timetableAlias: string } {
  const scopeKey = (scopeKeyRaw ?? '').trim();
  if (!scopeKey) return { trackName: '', timetableAlias: '' };
  const sep = scopeKey.indexOf('|');
  if (sep < 0) return { trackName: scopeKey, timetableAlias: '' };
  return {
    trackName: scopeKey.slice(0, sep).trim(),
    timetableAlias: scopeKey.slice(sep + 1).trim(),
  };
}

export type ScopedRowsAnnualIndex = {
  bySourceAnnualKey: Map<string, ProgramScopedContentRow[]>;
  trackRowsByScopeKey: Map<string, ProgramScopedContentRow[]>;
  projectRowsByScopeKey: Map<string, ProgramScopedContentRow[]>;
};

export function buildScopedRowsAnnualIndex(scopedRows: ProgramScopedContentRow[]): ScopedRowsAnnualIndex {
  const bySourceAnnualKey = new Map<string, ProgramScopedContentRow[]>();
  const trackRowsByScopeKey = new Map<string, ProgramScopedContentRow[]>();
  const projectRowsByScopeKey = new Map<string, ProgramScopedContentRow[]>();
  for (const r of scopedRows) {
    const src = (r.source_annual_cell_key ?? '').trim();
    if (src) {
      const arr = bySourceAnnualKey.get(src) ?? [];
      arr.push(r);
      bySourceAnnualKey.set(src, arr);
    }
    if (r.scope_type === 'track') {
      const sk = (r.scope_key ?? '').trim();
      if (sk) {
        const arr = trackRowsByScopeKey.get(sk) ?? [];
        arr.push(r);
        trackRowsByScopeKey.set(sk, arr);
      }
    } else if (r.scope_type === 'project') {
      const sk = (r.scope_key ?? '').trim();
      if (sk) {
        const arr = projectRowsByScopeKey.get(sk) ?? [];
        arr.push(r);
        projectRowsByScopeKey.set(sk, arr);
      }
    }
  }
  const sortTrack = compareContentRowsByOrder;
  trackRowsByScopeKey.forEach((arr) => {
    arr.sort(sortTrack);
  });
  projectRowsByScopeKey.forEach((arr) => {
    arr.sort(sortTrack);
  });
  return { bySourceAnnualKey, trackRowsByScopeKey, projectRowsByScopeKey };
}

/**
 * 편제·프로젝트 칸의 내용 행 = 그 스코프의 (순서대로 정렬한) N번째 행.
 * N(trackOccurrenceIndex)은 같은 스코프 일정 중 이 칸이 몇 번째인지. 셀 키로는 찾지 않는다.
 */
export function pickScopedContentFromAnnualIndex(
  index: ScopedRowsAnnualIndex,
  _annualCellKey: string,
  programKindLabel: string,
  templateLabel: string,
  timetableText: string,
  projectId: string | null,
  legacyTimetableText?: string,
  trackOccurrenceIndex?: number,
  preferredTrackScopeKey?: string
): ProgramScopedContentRow | null {
  void _annualCellKey;
  const occurrenceIdx =
    typeof trackOccurrenceIndex === 'number' && Number.isInteger(trackOccurrenceIndex) && trackOccurrenceIndex >= 0
      ? trackOccurrenceIndex
      : undefined;
  if (occurrenceIdx === undefined) return null;
  if (programKindLabel === '프로') {
    if (!projectId) return null;
    return index.projectRowsByScopeKey.get(projectId)?.[occurrenceIdx] ?? null;
  }
  const tl = templateLabel.trim();
  /** index 배열은 이미 compareContentRowsByOrder로 정렬돼 있다 */
  const rowsFor = (scopeKey: string) => index.trackRowsByScopeKey.get(scopeKey);
  const preferred = (preferredTrackScopeKey ?? '').trim();
  if (preferred) {
    const rows = rowsFor(preferred);
    if (rows) return rows[occurrenceIdx] ?? null;
  }
  const aliasCandidates = Array.from(
    new Set([timetableText, legacyTimetableText].map((v) => (v ?? '').trim()).filter(Boolean))
  );
  for (const alias of aliasCandidates) {
    const rows = rowsFor(`${tl}|${alias}`);
    if (rows) return rows[occurrenceIdx] ?? null;
  }
  const unified = rowsFor(`${tl}|-`);
  if (unified) return unified[occurrenceIdx] ?? null;
  return null;
}

/** 연간진도표·대시보드 공통: 편제 프로그램(통합/분리/동기화) scoped occurrence 키 */
export function computeScopedOccurrenceForAnnualSlot(input: {
  isFixedSlot: boolean;
  programKindLabel: string;
  projectId: string | null;
  templateLabel: string;
  scopeLookupAlias: string;
  trackProgramModeByTrack: Record<string, TrackProgramMode>;
  trackProgramSyncRepByTrack: Record<string, string>;
}): { scopedOccurrenceKey: string; preferredTrackScopeKey: string; scopeLookupKey: string } {
  const {
    isFixedSlot,
    programKindLabel,
    projectId,
    templateLabel,
    scopeLookupAlias,
    trackProgramModeByTrack,
    trackProgramSyncRepByTrack,
  } = input;
  const tl = templateLabel.trim();
  const alias = scopeLookupAlias.trim();
  const scopeLookupKey = tl && alias ? `${tl}|${alias}` : '';
  if (isFixedSlot) {
    return { scopedOccurrenceKey: '', preferredTrackScopeKey: '', scopeLookupKey };
  }
  if (programKindLabel === '프로' && projectId) {
    return { scopedOccurrenceKey: `project:${projectId}`, preferredTrackScopeKey: '', scopeLookupKey };
  }
  const trackModeForRow = tl ? trackProgramModeByTrack[tl] ?? 'unified' : 'unified';
  const preferredTrackScopeKey =
    programKindLabel !== '프로' && tl
      ? trackModeForRow === 'unified'
        ? `${tl}|-`
        : trackModeForRow === 'sync'
          ? `${tl}|${trackProgramSyncRepByTrack[tl]?.trim() || alias || '-'}`
          : scopeLookupKey
      : '';
  const trackKey = trackModeForRow === 'sync' ? scopeLookupKey : preferredTrackScopeKey || scopeLookupKey;
  const scopedOccurrenceKey = trackKey ? `track:${trackKey}` : '';
  return { scopedOccurrenceKey, preferredTrackScopeKey, scopeLookupKey };
}

/** 연간진도표 rows useMemo와 동일: scoped 우선, 레거시 program_progress_cells는 scoped 없을 때만(고정 등) */
export function resolveAnnualSlotProgressSource(input: {
  scopedRowsIndex: ScopedRowsAnnualIndex;
  annualCellKey: string;
  programKindLabel: string;
  isFixedSlot: boolean;
  hasTimetableAssignment: boolean;
  templateLabel: string;
  displayText: string;
  projectId: string | null;
  legacyPc?: ProgramProgressCell;
  trackProgramModeByTrack: Record<string, TrackProgramMode>;
  trackProgramSyncRepByTrack: Record<string, string>;
  scopedOccurrenceIndex?: number;
  preferredTrackScopeKey: string;
  scopeLookupKey?: string;
  fixedMatchByCell?: ProgramFixedContentRow | null;
  fixedMatchByEvent?: ProgramFixedContentRow | null;
}): ProgramProgressLikeRow | null {
  const {
    scopedRowsIndex,
    annualCellKey,
    programKindLabel,
    isFixedSlot,
    hasTimetableAssignment,
    templateLabel,
    displayText,
    projectId,
    legacyPc,
    trackProgramModeByTrack,
    trackProgramSyncRepByTrack: _trackProgramSyncRepByTrack,
    scopedOccurrenceIndex,
    preferredTrackScopeKey,
    scopeLookupKey: scopeLookupKeyInput,
    fixedMatchByCell,
    fixedMatchByEvent,
  } = input;
  void _trackProgramSyncRepByTrack;

  if (isFixedSlot) {
    const fixedRow = fixedMatchByCell ?? fixedMatchByEvent;
    return fixedRow
      ? mergeFixedContentWithLegacyProgramCell(fixedRow, legacyPc)
      : legacyPc
        ? programProgressCellToProgressLike(legacyPc)
        : null;
  }

  const shouldPickScopedRow =
    hasTimetableAssignment && (programKindLabel === '프로' ? !!projectId : !!templateLabel.trim());
  if (!shouldPickScopedRow) {
    return legacyPc ? programProgressCellToProgressLike(legacyPc) : null;
  }

  const scopeLookupKey = (scopeLookupKeyInput ?? '').trim();
  const isMirror = isSyncTrackMirrorScope(
    templateLabel,
    scopeLookupKey,
    preferredTrackScopeKey,
    trackProgramModeByTrack
  );
  if (!isMirror) {
    const contentRow = pickScopedContentFromAnnualIndex(
      scopedRowsIndex,
      annualCellKey,
      programKindLabel,
      templateLabel,
      displayText,
      projectId,
      legacyPc?.timetable_text ?? undefined,
      scopedOccurrenceIndex,
      preferredTrackScopeKey
    );
    /** 편제·프로젝트는 스코프 행만 본다(옛 program_progress_cells 본문을 섞지 않음) */
    return contentRow ? scopedRowToProgressLike(contentRow) : null;
  }

  const repContentRow = pickRepScopedContentRowForMirrorSlot(
    scopedRowsIndex,
    preferredTrackScopeKey,
    scopedOccurrenceIndex
  );
  const recordRow = pickScopedRecordRowFromAnnualIndex(
    scopedRowsIndex,
    annualCellKey,
    programKindLabel,
    templateLabel,
    displayText,
    projectId,
    legacyPc?.timetable_text ?? undefined,
    scopedOccurrenceIndex,
    scopeLookupKey,
    preferredTrackScopeKey,
    trackProgramModeByTrack
  );
  if (!repContentRow) return null;
  return mergeSyncTrackScopedRowsForDisplay(repContentRow, recordRow);
}

/**
 * computeScopedOccurrenceForAnnualSlot 의 scopedOccurrenceKey(`project:<id>` / `track:<scopeKey>`)를
 * 공통 매처용 일정 칸 참조로 바꾼다. 고정·빈 키는 null.
 */
export function scopedScheduleSlotRefFromOccurrenceKey(
  annualCellKey: string,
  scopedOccurrenceKey: string
): ScopedScheduleSlotRef | null {
  const key = (scopedOccurrenceKey ?? '').trim();
  if (!annualCellKey || !key) return null;
  if (key.startsWith('project:')) {
    const scopeKey = key.slice('project:'.length).trim();
    return scopeKey ? { annualCellKey, scopeType: 'project', scopeKey } : null;
  }
  if (key.startsWith('track:')) {
    const scopeKey = key.slice('track:'.length).trim();
    return scopeKey ? { annualCellKey, scopeType: 'track', scopeKey } : null;
  }
  return null;
}
